import { useRef, useCallback, useMemo, useState, useEffect } from 'react'
import type { MutableRefObject } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { format } from 'date-fns'
import { Calendar } from '@/components/ui/calendar'
import type { DateRange, Matcher } from 'react-day-picker'
import { buildMonths } from './calendar-months'

const START_DATE = '2010-01-01'

/** Jan 2010 → current month (browser-local, as before). */
const ALL_MONTHS = buildMonths(START_DATE, format(new Date(), 'yyyy-MM-dd'))

/** Average estimate — actual height measured dynamically via measureElement */
const MONTH_HEIGHT_ESTIMATE = 200

interface VirtualizedCalendarProps {
  draftRange: DateRange | undefined
  onRangeSelect: (range: DateRange | undefined) => void
  today: Date
  scrollToMonthRef: MutableRefObject<Date | null>
  onVisibleMonthChange: (month: Date) => void
  onDayClick?: (day: Date) => void
  /** Months to render. Defaults to Jan 2010 through the current month. */
  months?: Date[]
  /** Disabled days. Defaults to every day after `today`. */
  disabled?: Matcher | Matcher[]
}

export function VirtualizedCalendar({
  draftRange,
  onRangeSelect,
  today,
  scrollToMonthRef,
  onVisibleMonthChange,
  onDayClick,
  months = ALL_MONTHS,
  disabled,
}: VirtualizedCalendarProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const isScrollingProgrammatically = useRef(false)
  const pendingScrollIdx = useRef<number | null>(null)

  const virtualizer = useVirtualizer({
    count: months.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => MONTH_HEIGHT_ESTIMATE,
    overscan: 3,
    measureElement: (el) => el.getBoundingClientRect().height,
  })

  /** Perform a programmatic scroll, suppressing the visible-month handler. */
  const doScroll = useCallback(
    (idx: number) => {
      isScrollingProgrammatically.current = true
      virtualizer.scrollToIndex(idx, { align: 'start' })
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isScrollingProgrammatically.current = false
        })
      })
    },
    [virtualizer],
  )

  // Bump this to force a re-render after the scroll container mounts.
  // The virtualizer reads getScrollElement() during render, but the callback
  // ref fires after render (during commit), so we need a second render pass
  // for the virtualizer to pick up the new scroll element.
  const [scrollMountKey, setScrollMountKey] = useState(0)

  // Callback ref: store the scroll container and trigger a re-render so the
  // virtualizer can observe the new element.
  const scrollCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node

      // Consume any pending scroll from scrollToMonthRef
      if (node && scrollToMonthRef.current) {
        const target = scrollToMonthRef.current
        scrollToMonthRef.current = null
        const idx = months.findIndex(
          (m) => m.getFullYear() === target.getFullYear() && m.getMonth() === target.getMonth(),
        )
        if (idx >= 0) {
          pendingScrollIdx.current = idx
        }
      }

      if (node) {
        setScrollMountKey((k) => k + 1)
      }
    },
    [scrollToMonthRef, months],
  )

  // After the virtualizer has re-observed the scroll element (post re-render),
  // consume the pending scroll index and perform the actual scroll.
  useEffect(() => {
    if (scrollRef.current && pendingScrollIdx.current != null) {
      const idx = pendingScrollIdx.current
      pendingScrollIdx.current = null
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          doScroll(idx)
        })
      })
    }
  }, [scrollMountKey, doScroll])

  // Track visible month on scroll — suppressed during programmatic scrolls
  const handleScroll = useCallback(() => {
    if (isScrollingProgrammatically.current) return
    const items = virtualizer.getVirtualItems()
    if (items.length > 0) {
      const firstIdx = items[0].index
      onVisibleMonthChange(months[firstIdx])
    }
  }, [virtualizer, onVisibleMonthChange, months])

  // Handle scrollToMonthRef being set after the container is already mounted
  // (e.g., prev/next arrows, month-year picker). The callback ref only fires
  // on mount, so we consume the ref during render for the post-mount case.
  if (scrollRef.current && scrollToMonthRef.current) {
    const target = scrollToMonthRef.current
    scrollToMonthRef.current = null
    const idx = months.findIndex(
      (m) => m.getFullYear() === target.getFullYear() && m.getMonth() === target.getMonth(),
    )
    if (idx >= 0) {
      doScroll(idx)
    }
  }

  // Memoize disabled prop so Calendar doesn't re-render unnecessarily
  const disabledMatcher = useMemo(() => disabled ?? { after: today }, [disabled, today])

  return (
    <div
      ref={scrollCallbackRef}
      onScroll={handleScroll}
      className="overflow-y-auto"
      style={{ height: 340 }}
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const month = months[virtualRow.index]
          return (
            <div
              key={virtualRow.index}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {/* Our own month label */}
              <div className="px-2 pt-3 pb-0 text-xs font-semibold text-foreground">
                {format(month, 'MMMM yyyy').toUpperCase()}
              </div>
              <Calendar
                mode="range"
                month={month}
                numberOfMonths={1}
                hideNavigation
                selected={draftRange}
                onSelect={onDayClick ? () => {} : onRangeSelect}
                onDayClick={onDayClick}
                disabled={disabledMatcher}
                classNames={{ month_caption: 'hidden' }}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export { ALL_MONTHS }
